import { appContext } from "../../../../AppContext";
import { JSX } from "react";
import { Guid, Validator } from "@iconics/core";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { AlertDialog, Button, ButtonBar, Card, Dialog, DialogContent, DialogFooter, FileDropZone, GridContainer, GridItem, HelperText, RightButtons, Select } from "@iconics/components";
import { Accept } from "react-dropzone/.";
import { IImportResultWrapper } from "./DataModels";

export class VirtualTreeViewImportDialog extends IbssComponent<IProps, IState>
{
    private labels = appContext().labels;
    private validator = new Validator(appContext().core.validationOptions);

    constructor(props: IProps)
    {
        super(props);

        this.state =
        {
            selectedOntology: '',
            ontologyError: '',
            file: undefined,
            fileError: '',
            allowedFileTypes: {},
            options: [],
            uploadPath: '',
            resetFilesToken: Guid.new().toString(),
            showResult: false,
            partialSuccess: false,
        };
    }

    public async componentDidMount()
    {
        this.validator
            .addInput('selectedOntology',
                () => this.state.selectedOntology,
                i => this.setState({ ontologyError: i }))
            .isRequired();
        this.validator
            .addInput('file',
                () => this.state.file?.toString() ?? '',
                i => this.setState({ fileError: i }))
            .isRequired();
    }

    public componentDidUpdate(prevProps: IProps)
    {
        if (prevProps.open !== this.props.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    private handleOpen(): void
    {
        this.setState({
            selectedOntology: '',
            ontologyError: '',
            file: undefined,
            fileError: '',
            allowedFileTypes: {},
            resetFilesToken: Guid.new().toString(),
            showResult: false
        });
    }

    private onFileUploaded(file: File | null): void
    {
        this.setState({
            file: file ?? undefined,
        }, () => { if (file) this.validator.validate('file') });
    }

    private async handleImport(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        if (!this.state.file)
            return;
        const formData = new FormData();
        formData.append('file', this.state.file);

        await this.loading(async () =>
        {
            if (!this.state.selectedOntology) return;

            const result = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.import.post<IImportResultWrapper>({
                ontologyId: this.state.selectedOntology,
                tenancy: appContext().tenantId,
                body: formData,
            });
            console.log(`Import result:\n${JSON.stringify(result)}`);

            let partialSuccess = false;
            if (result.Failure.TreeViews.length > 0)
            {
                partialSuccess = true;
            }

            this.setState({ showResult: true, partialSuccess: partialSuccess });
        });
    }

    private handleResultDialogClose(): void
    {
        this.setState({
            showResult: false
        })
        this.props.onClose(true);
    }

    public render(): JSX.Element
    {
        return (
            <>
                <Dialog
                    isOpen={this.props.open}
                    onOpenChange={() => this.props.onClose(false)}
                >
                    <DialogContent>
                        <GridContainer
                            spacing='cards'
                        >
                            <GridItem>
                                <Card
                                    title={this.labels.Platform_VirtualTreeView_ImportDialog_Title}
                                    subTitle={this.labels.Platform_VirtualTreeView_ImportDialog_SubTitle}
                                >
                                    <GridContainer>
                                        <GridItem>
                                            <Select
                                                label={this.labels.Ontology_Name}
                                                placeholder={this.labels.Ontology_Name}
                                                value={this.state.selectedOntology ? [this.state.selectedOntology] : []}
                                                onSelect={e => this.setState({ selectedOntology: e.value }, () => this.validator.validate('selectedOntology'))}
                                                items={this.props.ontologies.map(x => ({ label: x.label, value: x.value.toString() }))}
                                                required
                                                invalid={!!this.state.ontologyError}
                                                invalidMessage={this.state.ontologyError}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </Card>
                            </GridItem>
                            <GridItem>
                                <Card
                                    title={this.labels.Platform_VirtualTreeView_ImportDialog_FileDropZone_Title}
                                    subTitle={this.labels.Platform_VirtualTreeView_ImportDialog_FileDropZone_SubTitle}
                                >
                                    <GridContainer>
                                        <GridItem>
                                            <FileDropZone
                                                existingFileNames={this.state.options}
                                                uploadPath={this.state.uploadPath}
                                                allowedFileTypes={this.state.allowedFileTypes}
                                                onFilesDropped={(file) => this.onFileUploaded(file)}
                                                previewFileOnDrop={true}
                                                resetFilesToken={this.state.resetFilesToken}
                                            />
                                            <GridItem>
                                            </GridItem>
                                            {!!this.state.fileError &&
                                                <HelperText error={true}>{this.state.fileError}</HelperText>
                                            }
                                        </GridItem>
                                    </GridContainer>
                                </Card>
                            </GridItem>
                        </GridContainer>
                    </DialogContent>
                    <DialogFooter>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    onClick={() => this.props.onClose(false)}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    styleType='filled'
                                    onClick={() => this.handleImport()}
                                    disabled={!this.validator.isValid}
                                >
                                    {this.labels.Platform_CommonAction_Import}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </DialogFooter>
                </Dialog>
                <AlertDialog
                    show={this.state.showResult}
                    title={this.state.partialSuccess ? this.labels.Platform_VirtualTreeView_ImportPartialSuccess :
                        this.labels.Platform_VirtualTreeView_ImportSuccess}
                    message={this.state.partialSuccess ? this.labels.Platform_VirtualTreeView_ImportPartialSuccess_Message :
                        this.labels.Platform_VirtualTreeView_ImportSuccess_Message}
                    onClose={() => this.handleResultDialogClose()}
                />
            </>
        );
    }
}

export interface IProps 
{
    open: boolean;
    ontologies: { value: string, label: string }[];
    onClose(success: boolean): void;
}

export interface IState
{
    selectedOntology: string;
    ontologyError: string;
    file: File | undefined;
    fileError: string;
    allowedFileTypes: Accept;
    options: string[];
    uploadPath: string;
    resetFilesToken: string;
    showResult: boolean;
    partialSuccess: boolean;
}
